import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import { ExclamationMarkIcon, XIcon } from '@repo/ui/icons'
import { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { useFilters } from '../../../../lib/hooks/use-filters'
import { AssignJourneyToCenterInput } from '../../../../lib/types/models/center/center-input'
import { CenterProperties } from '../../../../lib/types/models/center/properties.center'
import { CenterToJourneyProperties } from '../../../../lib/types/models/center-to-journey/properties.center-to-journey'
import { Journey, JourneyFilters } from '../../../../lib/types/models/product/journey'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformJourneyFiltersToUrl,
  transformUrlToJourneyFilters
} from '../../../../lib/utils/filters-mapping/journey'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form } from '../../../elements/forms/form'
import PeriodBadge from '../../../elements/period-badge'
import JourneyListSelection from '../../enrollments/enrolment-create-proposition-steps/journey-list-selection'
import JourneyListSelectionFilter from '../../enrollments/enrolment-create-proposition-steps/journey-list-selection-filter'
import { ASSIGN_JOURNEY_TO_CENTER_SCHEMA } from '../center.utils'
import AssignStaffRequest from './assign-staff-request'
import CenterJourneyInformation from './center-journey-information'
import CenterTripsInformation from './center-trips-information'

type Props = {
  step: number
  defaultValues?: AssignJourneyToCenterInput
  onStepChange: (step: number) => void
  onSubmit: (values: AssignJourneyToCenterInput) => void
  // set when editing an existing center: its stays are shown first
  centerId?: number
}

const CenterJourneySelectionList = ({
  step,
  defaultValues,
  onStepChange,
  onSubmit,
  centerId
}: Props) => {
  const [checkForErrors, setCheckForErrors] = useState(false)

  const form = useForm<AssignJourneyToCenterInput>({
    resolver: zodResolver(ASSIGN_JOURNEY_TO_CENTER_SCHEMA),
    defaultValues,
    mode: FORM_MODE
  })

  const journeyFilter = useFilters<JourneyFilters>({
    initialFilters: { published: true },
    options: { allowSearchParam: false },
    transformFilterToUrl: transformJourneyFiltersToUrl,
    transformUrlToFilter: transformUrlToJourneyFilters
  })

  const errorMessage = CenterProperties.checkPossibleErrorsPerStep(step, form.watch())

  const nextStep = (values?: AssignJourneyToCenterInput) => {
    setCheckForErrors(true)
    if (errorMessage) return
    if (step === 3 && values) return onSubmit(values)
    onStepChange(step + 1)
    setCheckForErrors(false)
  }
  const previousStep = () => {
    onStepChange(step - 1)
  }

  const centerToJourneys = form.watch('centerToJourneys')

  const onSelectJourneys = (journeys: Journey[]) => {
    const newCenterToJourneys = journeys.map((journey) => {
      const oldCenterJourney = centerToJourneys.find((c) => c.journey.id === journey.id)
      return {
        journey,
        maxNumberOfStudentPlaces: oldCenterJourney?.maxNumberOfStudentPlaces || 0,
        relatedTrips: oldCenterJourney?.relatedTrips || []
      }
    })

    // Get all main trip IDs from selected journeys
    const selectedMainTripIds = new Set(
      journeys.flatMap((j) => j.places.map((place) => place.mainTrip.id))
    )

    // Clear any related trips that don't belong to selected journeys
    const cleanedCenterToJourneys = newCenterToJourneys.map((centerJourney) => ({
      ...centerJourney,
      relatedTrips: centerJourney.relatedTrips.filter((trip) => selectedMainTripIds.has(trip.id))
    }))

    form.setValue('centerToJourneys', cleanedCenterToJourneys)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className={MODAL_CONTENT_CLASSNAME}>
          {errorMessage && checkForErrors && step !== 3 && (
            <>
              <Badge
                variant="danger"
                className="py-5 rounded"
                icon={
                  <div className="border border-red rounded-md w-fit p-0.5 rounded-full">
                    <ExclamationMarkIcon color={tailwindColors.red.DEFAULT} />
                  </div>
                }
                iconPosition="start"
                text={errorMessage}
              />
              <div className="h-4" />
            </>
          )}
          {step === 0 && (
            <>
              <div>
                <div className="h-4" />
                <JourneyListSelectionFilter
                  filter={journeyFilter.filter}
                  onResetFilter={journeyFilter.resetFilters}
                  onSelectedFilterChange={journeyFilter.onSelectedFilterChange}
                />
                <div className="h-3" />
              </div>
              {centerId !== undefined && centerToJourneys.length > 0 && (
                <div className="mb-4 flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                      Séjours sélectionnés
                    </Typography>
                    <Badge variant="grey" size="xsmall" text={String(centerToJourneys.length)} />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {centerToJourneys.map((centerToJourney) => (
                      <PeriodBadge
                        key={centerToJourney.journey.id}
                        period={centerToJourney.journey.date.period}
                        text={[
                          centerToJourney.journey.reference,
                          CenterToJourneyProperties.getTripDate(centerToJourney),
                          CenterToJourneyProperties.getFormulaName(centerToJourney),
                          CenterToJourneyProperties.getThemeName(centerToJourney),
                          ...CenterToJourneyProperties.getOptions(centerToJourney)
                        ].join(' - ')}
                        icon={
                          <button
                            type="button"
                            aria-label="Désélectionner le séjour"
                            className="flex items-center"
                            onClick={() =>
                              onSelectJourneys(
                                centerToJourneys
                                  .filter((c) => c.journey.id !== centerToJourney.journey.id)
                                  .map((c) => c.journey)
                              )
                            }
                          >
                            <XIcon
                              width="14"
                              height="14"
                              color={centerToJourney.journey.date.period?.color}
                            />
                          </button>
                        }
                      />
                    ))}
                  </div>
                </div>
              )}
              <JourneyListSelection
                tableTitle="Sélectionner un ou plusieurs séjour(s)"
                mode={'multiple'}
                journeys={centerToJourneys.map((j) => j.journey)}
                centerId={centerId}
                showHoursPerWeekForAllProducts
                filterProps={journeyFilter}
                onSubmit={() => nextStep()}
                onSelectJourneys={onSelectJourneys}
              />
            </>
          )}
          {step === 1 && centerToJourneys.length > 0 && (
            <CenterJourneyInformation
              buttonsConfig={modalButtonConfig({
                cancelText: 'Retour',
                confirmText: 'Suivant',
                onCancel: () => previousStep(),
                onConfirm: () => nextStep()
              })}
            />
          )}
          {step === 2 && (
            <AssignStaffRequest
              buttonsConfig={modalButtonConfig({
                cancelText: 'Retour',
                confirmText: 'Suivant',
                onCancel: () => previousStep(),
                onConfirm: () => {
                  // Remove the lines that don't have a qualification
                  const staffRequests = form
                    .getValues('staffRequests')
                    .filter((request) => request.qualification)
                  form.setValue('staffRequests', staffRequests)
                  nextStep()
                }
              })}
            />
          )}

          {step === 3 && (
            <CenterTripsInformation
              checkForErrors={checkForErrors}
              buttonsConfig={modalButtonConfig({
                cancelText: 'Retour',
                onCancel: () => previousStep(),
                onConfirm: (values: AssignJourneyToCenterInput) => nextStep(values)
              })}
            />
          )}
        </div>
      </form>
    </Form>
  )
}

export default CenterJourneySelectionList
